// Weekly site roster: one column per day with people, movements, trades, rooms and dietary needs.
import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { Spinner, ErrorBox } from '../ui';
import { todayDarwin, addDays, eachDate, fmtDayLabel, toAus, fullName } from '../util';

export default function WeekRoster() {
  const [start, setStart] = useState(() => {
    const t = todayDarwin();
    return addDays(t, -((new Date(t + 'T12:00:00Z').getUTCDay() + 6) % 7)); // Monday
  });
  const [data, setData] = useState<any | null>(null);
  const [demand, setDemand] = useState<any[]>([]);
  const [catering, setCatering] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  const end = addDays(start, 6);

  useEffect(() => {
    setData(null);
    Promise.all([
      api('/attendance'), api('/people'), api(`/demand?from=${start}&to=${end}`),
      api(`/catering?from=${start}&to=${end}`), api('/work_areas')
    ]).then(([att, people, dem, cat, areas]) => {
      setData({ att: att.filter((a: any) => a.status !== 'cancelled'), people, areas });
      setDemand(dem);
      setCatering(cat);
    }).catch(e => setError(e.message));
  }, [start]);

  if (error) return <ErrorBox error={error} />;
  if (!data || !catering) return <Spinner />;

  const names = Object.fromEntries(data.people.map((p: any) => [p.id, fullName(p)]));
  const tradeOf = Object.fromEntries(data.people.map((p: any) => [p.id, p.trade || '']));
  const areaName: Record<number, string> = Object.fromEntries(data.areas.map((a: any) => [a.id, a.name]));
  const eff = (p: any) => ({ ci: p.actual_check_in || p.check_in, co: p.actual_check_out || p.check_out });
  const onsiteOn = (d: string) => data.att.filter((p: any) => d >= eff(p).ci && d <= eff(p).co);

  return (
    <div className="page">
      <div className="page-head">
        <h2>Weekly site roster — {toAus(start)} to {toAus(end)}</h2>
        <div className="btn-row">
          <button className="btn" onClick={() => setStart(addDays(start, -7))}>← Previous week</button>
          <button className="btn" onClick={() => setStart(addDays(todayDarwin(), -((new Date(todayDarwin() + 'T12:00:00Z').getUTCDay() + 6) % 7)))}>This week</button>
          <button className="btn" onClick={() => setStart(addDays(start, 7))}>Next week →</button>
          <button className="btn" onClick={() => window.print()}>Print</button>
        </div>
      </div>
      <div className="table-wrap">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th style={{ minWidth: 130 }}></th>
                {eachDate(start, end).map(d => <th key={d}>{fmtDayLabel(d)}</th>)}
              </tr>
            </thead>
            <tbody>
              {row('People onsite', d => {
                const on = onsiteOn(d);
                return (
                  <>
                    <strong>{on.length}</strong>
                    <div className="muted" style={{ whiteSpace: 'normal' }}>
                      {on.map((p: any) => `${names[p.person_id]}${areaName[p.work_area_id] ? ` (${areaName[p.work_area_id]})` : ''}`).join(', ')}
                    </div>
                  </>
                );
              })}
              {row('By work area', d => {
                const counts: Record<string, number> = {};
                for (const p of onsiteOn(d)) {
                  const a = areaName[p.work_area_id] || 'Not assigned';
                  counts[a] = (counts[a] || 0) + 1;
                }
                const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
                return entries.length
                  ? <span style={{ whiteSpace: 'normal' }}>{entries.map(([a, n]) => `${a}: ${n}`).join(', ')}</span>
                  : '—';
              })}
              {row('Arrivals', d => {
                const list = data.att.filter((p: any) => (p.actual_check_in || p.check_in) === d);
                return list.length
                  ? <span style={{ whiteSpace: 'normal' }}>{list.map((p: any) => `${names[p.person_id]}${areaName[p.work_area_id] ? ` (${areaName[p.work_area_id]})` : ''}`).join(', ')}</span>
                  : '—';
              })}
              {row('Departures', d => {
                const list = data.att.filter((p: any) => (p.actual_check_out || p.check_out) === d);
                return list.length
                  ? <span style={{ whiteSpace: 'normal' }}>{list.map((p: any) => `${names[p.person_id]}${areaName[p.work_area_id] ? ` (${areaName[p.work_area_id]})` : ''}`).join(', ')}</span>
                  : '—';
              })}
              {row('Trades', d => {
                const counts: Record<string, number> = {};
                for (const p of onsiteOn(d)) {
                  const t = tradeOf[p.person_id] || 'Other';
                  counts[t] = (counts[t] || 0) + 1;
                }
                return Object.entries(counts).map(([t, n]) => `${t}: ${n}`).join(', ') || '—';
              })}
              {row('Supervisors', d => {
                const sups = [...new Set(onsiteOn(d).map((p: any) => p.supervisor).filter(Boolean))];
                return sups.join(', ') || '—';
              })}
              {row('Rooms required', d => demand.find(r => r.date === d)?.required ?? 0)}
              {row('Rooms booked', d => {
                const r = demand.find(x => x.date === d);
                if (!r) return 0;
                return <span style={{ color: r.shortfall > 0 ? 'var(--danger)' : undefined }}>{r.booked}{r.shortfall > 0 ? ` (short ${r.shortfall})` : ''}</span>;
              })}
              {row('Dietary', d => {
                const day = catering.days.find((x: any) => x.date === d);
                const reqs = day ? Object.entries(day.requirements) : [];
                return reqs.length ? reqs.map(([r, info]: any) => `${r}: ${info.count}${info.allergy ? ' ⚠' : ''}`).join(', ') : '—';
              })}
              {row('Notes', d => {
                const day = catering.days.find((x: any) => x.date === d);
                return day?.notes?.length ? <span style={{ whiteSpace: 'normal' }}>{day.notes.join('; ')}</span> : '—';
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  function row(label: string, cell: (d: string) => React.ReactNode) {
    return (
      <tr>
        <td><strong>{label}</strong></td>
        {eachDate(start, end).map(d => <td key={d} style={{ verticalAlign: 'top' }}>{cell(d)}</td>)}
      </tr>
    );
  }
}
